import { useEffect, useRef } from 'react';
import type { RefObject } from 'react';
import type { CountdownStage } from '../../modules/capture/types';

interface CountdownEffectsProps {
  videoRef: RefObject<HTMLVideoElement | null>;
  effect: CountdownStage['effect'];
  mirrored: boolean;
}

const vertexShaderSource = `#version 300 es
in vec2 a_position;
out vec2 v_uv;
void main() {
  v_uv = a_position * .5 + .5;
  gl_Position = vec4(a_position, 0., 1.);
}`;

const fragmentShaderSource = `#version 300 es
precision highp float;
uniform sampler2D u_video;
uniform vec2 u_resolution;
uniform vec2 u_videoSize;
uniform float u_time;
uniform float u_stage;
uniform float u_mirror;
in vec2 v_uv;
out vec4 outColor;

float hash(vec2 value) {
  return fract(sin(dot(value, vec2(127.1, 311.7))) * 43758.5453);
}

vec2 coverUv(vec2 uv) {
  float sourceAspect = u_videoSize.x / max(u_videoSize.y, 1.);
  float targetAspect = u_resolution.x / max(u_resolution.y, 1.);
  if (sourceAspect > targetAspect) {
    float visible = targetAspect / sourceAspect;
    uv.x = (uv.x - .5) * visible + .5;
  } else {
    float visible = sourceAspect / targetAspect;
    uv.y = (uv.y - .5) * visible + .5;
  }
  if (u_mirror > .5) uv.x = 1. - uv.x;
  return uv;
}

vec3 thermal(float value) {
  vec3 cold = vec3(.025, .035, .04);
  vec3 rust = vec3(.52, .095, .018);
  vec3 orange = vec3(.91, .35, .11);
  vec3 bone = vec3(.91, .91, .88);
  vec3 first = mix(cold, rust, smoothstep(.08, .48, value));
  vec3 second = mix(first, orange, smoothstep(.42, .74, value));
  return mix(second, bone, smoothstep(.76, .98, value));
}

void main() {
  vec2 uv = v_uv;
  vec2 sampleUv = coverUv(uv);
  vec2 tileCount = u_resolution.x > u_resolution.y ? vec2(8., 5.) : vec2(5., 8.);
  vec2 tileId = floor(uv * tileCount);
  vec2 tileUv = fract(uv * tileCount);
  float variant = mod(tileId.x + tileId.y * 2., 4.);
  float pulse = .5 + .5 * sin(u_time * .004 + tileId.x * .7 + tileId.y * .43);
  vec2 texel = 1. / max(u_videoSize, vec2(1.));
  vec3 source = texture(u_video, sampleUv).rgb;
  float luminance = dot(source, vec3(.299, .587, .114));
  float edge = abs(luminance - dot(texture(u_video, sampleUv + texel * vec2(2., 0.)).rgb, vec3(.299, .587, .114)));
  vec3 color;

  if (u_stage < .5) {
    float cells = mix(32., 58., step(2., variant));
    vec2 pointUv = floor(sampleUv * cells) / cells;
    vec3 points = texture(u_video, pointUv).rgb;
    float dotMask = 1. - smoothstep(.2, .49, length(fract(uv * cells) - .5));
    color = mix(vec3(dot(points, vec3(.299, .587, .114))) * .42, points, .28);
    color += vec3(.91, .35, .11) * dotMask * (.12 + edge * 2.4 + pulse * .08);
  } else if (u_stage < 1.5) {
    float shifted = clamp(luminance + (variant - 1.5) * .055, 0., 1.);
    color = thermal(shifted);
    color = mix(color, source * vec3(.62, .72, .78), step(2.5, variant) * .34);
  } else if (u_stage < 2.5) {
    vec2 pixels = floor(sampleUv * mix(150., 92., step(2., variant))) / mix(150., 92., step(2., variant));
    float mono = dot(texture(u_video, pixels).rgb, vec3(.299, .587, .114));
    float noise = hash(floor(gl_FragCoord.xy / mix(2., 4., step(2., variant))) + floor(u_time * .006));
    float bit = step(noise * .72 + .14, mono);
    vec3 dither = mix(vec3(.018), vec3(.91), bit);
    dither = mix(dither, vec3(.91, .35, .11), step(.86, mono + edge * 1.8) * .42);
    color = mix(source * vec3(.7, .73, .74), dither, .68);
  } else {
    color = mix(source, vec3(1.), .72);
  }

  float gutter = min(min(tileUv.x, 1. - tileUv.x), min(tileUv.y, 1. - tileUv.y));
  float panel = smoothstep(.006, .022, gutter);
  color *= mix(.42, 1., panel);
  float scan = .93 + .07 * sin(gl_FragCoord.y * 1.34 + u_time * .025);
  color *= scan;
  outColor = vec4(color, u_stage > 2.5 ? .68 : .76);
}`;

const stageNumber: Record<CountdownStage['effect'], number> = {
  particle: 0,
  thermal: 1,
  dither: 2,
  flash: 3,
};

function createShader(gl: WebGL2RenderingContext, type: number, source: string) {
  const shader = gl.createShader(type);
  if (!shader) return null;
  gl.shaderSource(shader, source);
  gl.compileShader(shader);
  if (!gl.getShaderParameter(shader, gl.COMPILE_STATUS)) {
    gl.deleteShader(shader);
    return null;
  }
  return shader;
}

export function CountdownEffects({ videoRef, effect, mirrored }: CountdownEffectsProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const effectRef = useRef(effect);
  effectRef.current = effect;

  useEffect(() => {
    const canvas = canvasRef.current;
    const video = videoRef.current;
    if (!canvas || !video || window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    const gl = canvas.getContext('webgl2', { alpha: true, antialias: false, powerPreference: 'high-performance' });
    if (!gl) return;

    const vertex = createShader(gl, gl.VERTEX_SHADER, vertexShaderSource);
    const fragment = createShader(gl, gl.FRAGMENT_SHADER, fragmentShaderSource);
    const program = gl.createProgram();
    if (!vertex || !fragment || !program) return;
    gl.attachShader(program, vertex);
    gl.attachShader(program, fragment);
    gl.linkProgram(program);
    if (!gl.getProgramParameter(program, gl.LINK_STATUS)) return;

    const position = gl.getAttribLocation(program, 'a_position');
    const buffer = gl.createBuffer();
    const texture = gl.createTexture();
    if (!buffer || !texture) return;
    gl.bindBuffer(gl.ARRAY_BUFFER, buffer);
    gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([-1, -1, 1, -1, -1, 1, -1, 1, 1, -1, 1, 1]), gl.STATIC_DRAW);
    gl.bindTexture(gl.TEXTURE_2D, texture);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
    gl.useProgram(program);
    gl.enableVertexAttribArray(position);
    gl.vertexAttribPointer(position, 2, gl.FLOAT, false, 0, 0);
    gl.enable(gl.BLEND);
    gl.blendFunc(gl.SRC_ALPHA, gl.ONE_MINUS_SRC_ALPHA);

    const resolutionLocation = gl.getUniformLocation(program, 'u_resolution');
    const videoSizeLocation = gl.getUniformLocation(program, 'u_videoSize');
    const timeLocation = gl.getUniformLocation(program, 'u_time');
    const stageLocation = gl.getUniformLocation(program, 'u_stage');
    const mirrorLocation = gl.getUniformLocation(program, 'u_mirror');
    let animationFrame = 0;
    let stopped = false;

    const render = (time: number) => {
      if (stopped) return;
      const rect = canvas.getBoundingClientRect();
      const scale = Math.min(window.devicePixelRatio || 1, 1.5, 720 / Math.max(rect.width, rect.height, 1));
      const width = Math.max(1, Math.round(rect.width * scale));
      const height = Math.max(1, Math.round(rect.height * scale));
      if (canvas.width !== width || canvas.height !== height) {
        canvas.width = width;
        canvas.height = height;
        gl.viewport(0, 0, width, height);
      }
      if (video.readyState >= HTMLMediaElement.HAVE_CURRENT_DATA && video.videoWidth > 0) {
        gl.bindTexture(gl.TEXTURE_2D, texture);
        gl.pixelStorei(gl.UNPACK_FLIP_Y_WEBGL, 1);
        gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, gl.RGBA, gl.UNSIGNED_BYTE, video);
        gl.uniform2f(resolutionLocation, width, height);
        gl.uniform2f(videoSizeLocation, video.videoWidth, video.videoHeight);
        gl.uniform1f(timeLocation, time);
        gl.uniform1f(stageLocation, stageNumber[effectRef.current]);
        gl.uniform1f(mirrorLocation, mirrored ? 1 : 0);
        gl.drawArrays(gl.TRIANGLES, 0, 6);
      }
      animationFrame = window.requestAnimationFrame(render);
    };
    animationFrame = window.requestAnimationFrame(render);

    return () => {
      stopped = true;
      window.cancelAnimationFrame(animationFrame);
      gl.deleteTexture(texture);
      gl.deleteBuffer(buffer);
      gl.deleteProgram(program);
      gl.deleteShader(vertex);
      gl.deleteShader(fragment);
    };
  }, [mirrored, videoRef]);

  return <canvas ref={canvasRef} className="countdown-shader" aria-hidden="true"/>;
}
